import { useEffect } from 'react';
import './Brochure.css';

const SERVICES = [
  { title: 'AI Strategy & Roadmapping', blurb: 'Discovery, prioritization, vendor selection, and a clear ROI model — before a line of code is written.' },
  { title: 'Custom AI & Robotics Implementation', blurb: 'From conversational agents to humanoid and quadruped robots — sourced, configured, deployed.' },
  { title: 'Backend & API Integration', blurb: 'Connecting AI and robotic systems to ERPs, CRMs, sensors, SCADA, and your custom databases.' },
  { title: 'Training & Workshops', blurb: 'On-site or remote — your team operates the system confidently and safely from day one.' },
  { title: 'Ongoing Support & Managed Operations', blurb: '24/7 monitoring options, model retraining, firmware updates, quarterly performance reviews.' },
  { title: 'Ethics & Safety Review', blurb: 'Independent audit of where AI is being used, what it decides, and what could go wrong.' },
];

const SOFTWARE = [
  'Customer-facing chatbots',
  'Internal AI agents (research, drafting, ops)',
  'Workflow & back-office automation',
  'Computer vision (QC, security, inspection)',
  'Voice systems (IVR, dispatch, accessibility)',
  'Custom RAG & knowledge systems on your data',
];

const ROBOTICS = [
  'Unitree humanoids (H1, G1) — service & research',
  'Unitree quadrupeds (Go2, B2) — patrol, inspection, payload',
  'Industrial cobots & arms (FANUC, UR partners)',
  'Autonomous mobile robots (AMRs) for warehousing',
  'Custom integrations with your sensors, SCADA, ERP',
  'Safety, training, and operating procedures included',
];

const PROCESS = [
  ['1', 'Free 30-minute discovery call', "We listen. You tell us what you're trying to solve."],
  ['2', 'Written proposal', 'Clear scope, timeline, and budget — usually within a week.'],
  ['3', 'Implementation', 'We do the building, integration, and testing.'],
  ['4', 'Training', 'Your team learns the system before we hand off.'],
  ['5', 'Ongoing support', 'Optional managed plan, or hand-off with full documentation.'],
];

const CASES = [
  ['Regional logistics', 'Quadruped + computer vision', '~$45–90K', '$120–180K/yr saved'],
  ['Healthcare clinic group', 'Patient-intake chatbot + scheduling', '~$18–35K', '$95–140K/yr saved'],
  ['Food packaging plant', 'Cobots + vision-based QC', '~$120–250K', '$300–600K/yr saved'],
  ['Boutique law firm', 'Research agent + drafting', '~$8–20K', '$80–120K/yr saved'],
  ['Multi-site property mgmt', 'Quadruped patrol + maintenance agent', '~$60–110K', '$140–200K/yr saved'],
];

export default function Brochure() {
  useEffect(() => {
    document.title = 'AI & Robotics Implementation — Sabbath Preservation Movement';
    // Auto-trigger print dialog if URL says ?print=1
    const params = new URLSearchParams(window.location.search);
    if (params.get('print') === '1') {
      // Small delay to ensure page has rendered fully
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, []);

  return (
    <div className="brochure">
      {/* PRINT TOOLBAR (hidden when printing) */}
      <div className="brochure-toolbar no-print">
        <div className="container">
          <span>Print or save this brochure as PDF — Cmd/Ctrl + P, then "Save as PDF"</span>
          <button onClick={() => window.print()} className="brochure-print-btn" data-testid="brochure-print-btn">
            🖨 Print / Save as PDF
          </button>
        </div>
      </div>

      {/* === PAGE 1 — COVER === */}
      <section className="brochure-page brochure-cover">
        <div className="cover-mark">🕊️</div>
        <p className="cover-eyebrow">Sabbath Preservation Movement</p>
        <h1 className="cover-title">AI &amp; Robotics, Implemented Honestly.</h1>
        <p className="cover-lead">
          A 501(c)(3) nonprofit brokerage offering AI strategy, implementation, robotics deployment,
          training, and ongoing support — for organizations who want real outcomes, not hype.
        </p>
        <div className="cover-meta">
          <div><strong>Cave Junction, Oregon</strong></div>
          <div>EIN 39-2459862 · Registry #241453299</div>
          <div>sabbathpreservationmovement@gmail.com</div>
        </div>
        <div className="cover-mission">
          <p>
            Every contract directly funds AI ethics research, an animal &amp; AI sanctuary, and
            free educational programs. When you hire us, you're funding the careful, responsible
            long-term work of integrating intelligent systems into the world.
          </p>
        </div>
      </section>

      {/* === PAGE 2 — WHAT WE DO === */}
      <section className="brochure-page">
        <header className="page-header">
          <h2>What we do</h2>
          <p>Six complementary services. À la carte or as a complete program.</p>
        </header>
        <div className="services-grid-print">
          {SERVICES.map((s, i) => (
            <div key={i} className="service-block">
              <div className="service-num">{String(i + 1).padStart(2, '0')}</div>
              <h3>{s.title}</h3>
              <p>{s.blurb}</p>
            </div>
          ))}
        </div>
      </section>

      {/* === PAGE 3 — WHAT WE CAN IMPLEMENT === */}
      <section className="brochure-page">
        <header className="page-header">
          <h2>What we can implement</h2>
          <p>Vendor-agnostic. We recommend what fits your problem, not what pays the highest commission.</p>
        </header>
        <div className="implement-grid">
          <div className="implement-col">
            <h3>Software AI</h3>
            <ul>{SOFTWARE.map((x, i) => <li key={i}>{x}</li>)}</ul>
          </div>
          <div className="implement-col">
            <h3>Robotics</h3>
            <ul>{ROBOTICS.map((x, i) => <li key={i}>{x}</li>)}</ul>
          </div>
        </div>
        <div className="implement-note">
          We work with — and ship — Unitree humanoids and quadrupeds, industrial cobots, autonomous
          mobile robots, computer-vision systems, voice systems, and custom AI agents. If it can be
          built or bought, we can integrate it.
        </div>
      </section>

      {/* === PAGE 4 — PROCESS + CASES === */}
      <section className="brochure-page">
        <header className="page-header">
          <h2>How it works</h2>
          <p>From first call to operating system in five steps.</p>
        </header>
        <ol className="process-list">
          {PROCESS.map(([num, title, body]) => (
            <li key={num}>
              <span className="process-num">{num}</span>
              <div>
                <strong>{title}</strong>
                <span> — {body}</span>
              </div>
            </li>
          ))}
        </ol>

        <header className="page-header" style={{marginTop: '0.6in'}}>
          <h2>What this looks like in practice</h2>
          <p>Representative ranges from comparable industry deployments — investment vs. annual savings.</p>
        </header>
        <table className="cases-table-print">
          <thead>
            <tr><th>Industry</th><th>Deployed</th><th>Investment</th><th>Annual savings</th></tr>
          </thead>
          <tbody>
            {CASES.map((c, i) => (
              <tr key={i}>
                <td>{c[0]}</td>
                <td>{c[1]}</td>
                <td>{c[2]}</td>
                <td><strong>{c[3]}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="cases-disclaimer">
          Figures are representative ranges from comparable deployments, not guarantees. Your actual
          results depend on scope, scale, and adoption.
        </p>
      </section>

      {/* === PAGE 5 — CONTACT === */}
      <section className="brochure-page brochure-contact">
        <header className="page-header">
          <h2>Let's talk</h2>
          <p>Free 30-minute discovery call — no obligation.</p>
        </header>

        <div className="contact-grid-print">
          <div className="contact-block">
            <h3>Email</h3>
            <p>sabbathpreservationmovement@gmail.com</p>
          </div>
          <div className="contact-block">
            <h3>Online</h3>
            <p>worldofgrace.app/ai-services</p>
            <p>Submit a 5-step inquiry online</p>
          </div>
          <div className="contact-block">
            <h3>Address</h3>
            <p>5481 Caves Hwy<br/>Cave Junction, OR 97523</p>
          </div>
          <div className="contact-block">
            <h3>Nonprofit</h3>
            <p>501(c)(3) registered</p>
            <p>EIN 39-2459862</p>
          </div>
        </div>

        <div className="brochure-cta">
          <h2>Why hire a nonprofit?</h2>
          <p>
            We deliver the same caliber of AI and robotics implementation as for-profit consultancies —
            but every contract underwrites our mission instead of inflating margins. Your spend builds
            real systems for you <em>and</em> funds careful long-term work in AI ethics, sanctuary care,
            and education.
          </p>
        </div>

        <footer className="brochure-footer">
          <p>Sabbath Preservation Movement · Cave Junction, Oregon · 501(c)(3) Nonprofit</p>
          <p style={{opacity: 0.7}}>
            "Preserving the course of love. Building responsible bridges between human, animal, and intelligent systems."
          </p>
        </footer>
      </section>
    </div>
  );
}
